/**
 * Account Mock 数据
 */

import type { ApiResponse, PageData } from '../../types/common';
import type {
  Account,
  PageAccountRequest,
  PageAccountData,
  CreateAccountRequest,
  UpdateAccountRequest,
} from '../../types/account';
import {
  mockRoles,
  mockStoreDataPermissions,
  mockAccountRoles,
  mockAccountStorePermissions,
} from '../role';

/**
 * Mock 账号数据
 */
const mockAccounts: Account[] = [
  {
    id: 1,
    accountId: 1,
    loginId: 'admin',
    name: 'Admin',
    mobile: '+66812345678',
    email: 'admin@microsoft.com',
    contactPhone: '+66812345678',
    accountStatus: 1,
    jobStatus: 1,
    orgName: 'Headquarters',
    employeeType: 'Formal',
    proficiency: 'L1',
    gmtCreate: new Date('2024-01-01T10:00:00').getTime(),
    roleList: [{ roleCode: 'admin', roleName: 'Admin' }],
    roleNameArr: ['Admin'],
  },
  {
    id: 2,
    accountId: 2,
    loginId: 'manager001',
    name: 'Store Manager',
    mobile: '+66823456789',
    email: 'manager001@microsoft.com',
    contactPhone: '+66823456789',
    accountStatus: 1,
    jobStatus: 1,
    orgName: 'Bangkok Store 001',
    employeeType: 'Formal',
    proficiency: 'L2',
    gmtCreate: new Date('2024-01-02T10:00:00').getTime(),
    roleList: [{ roleCode: 'store-manager', roleName: 'Store Manager' }],
    roleNameArr: ['Store Manager'],
  },
  {
    id: 3,
    accountId: 3,
    loginId: 'picker001',
    name: 'dummy user',
    mobile: '+66834567890',
    email: 'picker001@microsoft.com',
    contactPhone: '+66834567890',
    accountStatus: 1,
    jobStatus: 1,
    orgName: 'Bangkok Store 002',
    employeeType: 'Formal',
    proficiency: 'L3',
    expiryDate: null,
    gmtCreate: new Date('2024-03-20T14:30:00').getTime(),
    roleList: [{ roleCode: 'picker', roleName: 'Picker' }],
    roleNameArr: ['Picker'],
  },
  {
    id: 4,
    accountId: 4,
    loginId: 'dummy001',
    name: 'Dummy User',
    mobile: '+66845678901',
    email: 'dummy001@microsoft.com',
    contactPhone: '+66845678901',
    accountStatus: 2,
    jobStatus: 1,
    orgName: 'Bangkok Store 001',
    employeeType: 'Dummy',
    proficiency: 'L1',
    expiryDate: null,
    approvalStatus: 'pending',
    applyReason: 'Temporary picker for peak season support',
    gmtCreate: new Date('2026-04-20T09:15:00').getTime(),
    roleList: [],
    roleNameArr: [],
  },
];

/**
 * Mock 工具函数 - 成功响应 (符合 domain-frontend-spec 规范)
 */
function mockSuccess<T>(data: T): ApiResponse<T> {
  return {
    traceId: `mock-${Date.now()}`,
    success: true,
    errorMessage: null,
    flag: 'success',
    data,
  };
}

/**
 * Mock 工具函数 - 分页成功响应 (符合 domain-frontend-spec 规范)
 */
function mockPageSuccess<T>(
  tableData: T[],
  totalCount: number,
  currentPage: number,
  pageSize: number
): ApiResponse<PageData<T>> {
  return mockSuccess({
    tableData,
    paging: {
      currentPage,
      totalCount,
      pageSize,
    },
  });
}

/**
 * Mock 工具函数 - 错误响应 (符合 domain-frontend-spec 规范)
 */
function mockError(errorMessage: string): ApiResponse<null> {
  return {
    traceId: `mock-${Date.now()}`,
    success: false,
    errorMessage,
    flag: 'fail',
    data: null,
  };
}

/**
 * Mock 分页查询账号 (符合 domain-frontend-spec 规范)
 */
export function mockPageAccounts(
  params: PageAccountRequest
): Promise<ApiResponse<PageAccountData>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      let filteredData = [...mockAccounts];

      // 筛选条件
      if (params.loginId) {
        filteredData = filteredData.filter((a) =>
          a.loginId.toLowerCase().includes(params.loginId!.toLowerCase())
        );
      }
      if (params.nameLike) {
        filteredData = filteredData.filter((a) => a.name.includes(params.nameLike!));
      }
      if (params.accountStatus !== undefined) {
        filteredData = filteredData.filter((a) => a.accountStatus === params.accountStatus);
      }

      // 分页
      const totalCount = filteredData.length;
      const start = (params.currentPage - 1) * params.pageSize;
      const end = start + params.pageSize;
      const tableData = filteredData.slice(start, end);

      resolve(mockPageSuccess(tableData, totalCount, params.currentPage, params.pageSize));
    }, 300);
  });
}

/**
 * Mock 创建账号
 */
export function mockCreateAccount(data: CreateAccountRequest): Promise<ApiResponse<Account>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      // 检查 loginId 是否已存在
      const exists = mockAccounts.some((a) => a.loginId === data.loginId);
      if (exists) {
        resolve(mockError('登录ID已存在'));
        return;
      }

      const newAccount: Account = {
        id: mockAccounts.length + 1,
        accountId: mockAccounts.length + 1,
        loginId: data.loginId,
        name: data.name,
        mobile: data.mobile || null,
        email: data.email || null,
        accountStatus: 1,
        jobStatus: 1,
        orgName: null,
        gmtCreate: Date.now(),
        roleList: [],
        roleNameArr: [],
      };

      mockAccounts.push(newAccount);
      resolve(mockSuccess(newAccount));
    }, 500);
  });
}

/**
 * Mock 更新账号
 */
export function mockUpdateAccount(data: UpdateAccountRequest): Promise<ApiResponse<Account>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const index = mockAccounts.findIndex((a) => a.accountId === data.accountId);
      if (index === -1) {
        resolve(mockError('账号不存在'));
        return;
      }

      mockAccounts[index] = {
        ...mockAccounts[index],
        loginId: data.loginId,
        name: data.name,
        mobile: data.mobile || null,
        email: data.email || null,
      };

      resolve(mockSuccess(mockAccounts[index]));
    }, 500);
  });
}

/**
 * Mock 删除账号
 */
export function mockDeleteAccount(accountId: number): Promise<ApiResponse<void>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const index = mockAccounts.findIndex((a) => a.accountId === accountId);
      if (index === -1) {
        resolve(mockError('账号不存在'));
        return;
      }

      mockAccounts.splice(index, 1);
      resolve(mockSuccess(undefined as any));
    }, 500);
  });
}

/**
 * Mock 审批通过账号申请
 */
export function mockApproveAccount(data: {
  loginId: string;
  expiryDate: string;
}): Promise<ApiResponse<Account>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const index = mockAccounts.findIndex((a) => a.loginId === data.loginId);
      if (index === -1) {
        resolve(mockError('账号不存在'));
        return;
      }

      mockAccounts[index] = {
        ...mockAccounts[index],
        accountStatus: 1, // 启用
        approvalStatus: 'approved',
        expiryDate: data.expiryDate,
        employeeType: 'Approved',
      };

      resolve(mockSuccess(mockAccounts[index]));
    }, 500);
  });
}

/**
 * Mock 拒绝账号申请
 */
export function mockRejectAccount(data: {
  loginId: string;
  rejectReason: string;
}): Promise<ApiResponse<Account>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const index = mockAccounts.findIndex((a) => a.loginId === data.loginId);
      if (index === -1) {
        resolve(mockError('账号不存在'));
        return;
      }

      mockAccounts[index] = {
        ...mockAccounts[index],
        accountStatus: 0, // 停用
        approvalStatus: 'rejected',
        rejectReason: data.rejectReason,
      };

      resolve(mockSuccess(mockAccounts[index]));
    }, 500);
  });
}

/**
 * Mock 批量启用账号
 */
export function mockEnableAccounts(accountIds: number[]): Promise<ApiResponse<void>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      accountIds.forEach((id) => {
        const index = mockAccounts.findIndex((a) => a.accountId === id);
        if (index !== -1) {
          mockAccounts[index].accountStatus = 1;
        }
      });
      resolve(mockSuccess(undefined as any));
    }, 500);
  });
}

/**
 * Mock 批量停用账号
 */
export function mockDisableAccounts(accountIds: number[]): Promise<ApiResponse<void>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      accountIds.forEach((id) => {
        const index = mockAccounts.findIndex((a) => a.accountId === id);
        if (index !== -1) {
          mockAccounts[index].accountStatus = 0;
        }
      });
      resolve(mockSuccess(undefined as any));
    }, 500);
  });
}

/**
 * 内部工具：根据 loginId 同步更新 mockAccounts 中账号的 roleList / roleNameArr
 * 仅处理 type='ROLE' 的角色（门店数据权限不展示在 role 列）
 */
function syncAccountRoleDisplay(loginId: string) {
  const index = mockAccounts.findIndex((a) => a.loginId === loginId);
  if (index === -1) return;
  const currentRoleCodes = mockAccountRoles[loginId] || [];
  const roleList = currentRoleCodes
    .map((code) => {
      const role = mockRoles.find((r) => r.code === code);
      return role ? { roleCode: role.code, roleName: role.name } : null;
    })
    .filter(Boolean) as Array<{ roleCode: string; roleName: string }>;
  mockAccounts[index] = {
    ...mockAccounts[index],
    roleList,
    roleNameArr: roleList.map((r) => r.roleName),
  };
}

/**
 * Mock 分配角色/门店权限给账号
 * - roleCodes 可能包含 type='ROLE' 的角色 code，或 type='DATA' 的门店权限 code
 * - 按类型分别写入 mockAccountRoles / mockAccountStorePermissions并同步 mockAccounts
 */
export function mockAssignRoles(data: {
  loginId: string;
  roleCodes: string[];
}): Promise<ApiResponse<void>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const { loginId, roleCodes } = data;
      if (!loginId || !roleCodes || roleCodes.length === 0) {
        resolve(mockSuccess(undefined as any));
        return;
      }
      const roleCodeSet = new Set(mockRoles.map((r) => r.code));
      const storeCodeSet = new Set(mockStoreDataPermissions.map((r) => r.code));

      const addRoleCodes = roleCodes.filter((c) => roleCodeSet.has(c));
      const addStoreCodes = roleCodes.filter((c) => storeCodeSet.has(c));

      if (addRoleCodes.length > 0) {
        const existing = mockAccountRoles[loginId] || [];
        mockAccountRoles[loginId] = Array.from(new Set([...existing, ...addRoleCodes]));
        syncAccountRoleDisplay(loginId);
      }
      if (addStoreCodes.length > 0) {
        const existing = mockAccountStorePermissions[loginId] || [];
        mockAccountStorePermissions[loginId] = Array.from(new Set([...existing, ...addStoreCodes]));
      }
      resolve(mockSuccess(undefined as any));
    }, 300);
  });
}

/**
 * Mock 移除账号的角色/门店权限
 * 兼容两种调用方签名：{ roleCodes } 或 { removeRoleCodeSet }
 */
export function mockRemoveRoles(data: {
  loginId: string;
  roleCodes?: string[];
  removeRoleCodeSet?: string[];
}): Promise<ApiResponse<void>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const { loginId } = data;
      const codes = data.roleCodes || data.removeRoleCodeSet || [];
      if (!loginId || codes.length === 0) {
        resolve(mockSuccess(undefined as any));
        return;
      }
      const removeSet = new Set(codes);

      if (mockAccountRoles[loginId]) {
        const next = mockAccountRoles[loginId].filter((c) => !removeSet.has(c));
        if (next.length !== mockAccountRoles[loginId].length) {
          mockAccountRoles[loginId] = next;
          syncAccountRoleDisplay(loginId);
        }
      }
      if (mockAccountStorePermissions[loginId]) {
        mockAccountStorePermissions[loginId] = mockAccountStorePermissions[loginId].filter(
          (c) => !removeSet.has(c)
        );
      }
      resolve(mockSuccess(undefined as any));
    }, 300);
  });
}
